import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    IconButton,
    Alert,
    Grid,
    useTheme,
    CircularProgress,
    Container,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddIcon from "@mui/icons-material/Add";
import { PlatformTabs, TabPanel } from "../../components/TabPanel";
import useAuthApi from "../../context/hooks";
import OverlapData from "./Overlapdata";
import API_ENDPOINTS from "../../apis/endpoints";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "../../components/Card";

// eslint-disable-next-line react/display-name
const InputFields = React.memo(
    ({
        platform,
        tempUsernames,
        handleUsernameChange,
        handleRemoveUsername,
    }) => {
        return (
            <Grid container spacing={2}>
                {Array.from({
                    length: Math.ceil(tempUsernames.length / 2),
                }).map((_, rowIndex) => (
                    <Grid container item spacing={2} key={rowIndex}>
                        {[0, 1].map((colIndex) => {
                            const index = rowIndex * 2 + colIndex;
                            if (index >= tempUsernames.length) return null;

                            return (
                                <Grid item xs={6} key={index}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mb: 1 }}
                                    >
                                        Username {index + 1}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: 1,
                                            alignItems: "center",
                                            position: "relative",
                                        }}
                                    >
                                        <TextField
                                            fullWidth
                                            value={tempUsernames[index]}
                                            placeholder="username"
                                            onChange={(e) =>
                                                handleUsernameChange(
                                                    index,
                                                    e.target.value
                                                )
                                            }
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "8px",
                                                    pl: "12.4rem",
                                                },
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                position: "absolute",
                                                ml: "1rem",
                                            }}
                                        >
                                            {platform?.toLowerCase() ===
                                            "youtube"
                                                ? "https://youtube.com/@"
                                                : "https://instagram.com/"}
                                        </Typography>
                                        {tempUsernames.length > 2 && (
                                            <IconButton
                                                onClick={() =>
                                                    handleRemoveUsername(index)
                                                }
                                                sx={{ color: "error.main" }}
                                            >
                                                <DeleteOutlineRoundedIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                ))}
            </Grid>
        );
    }
);

const OverlapFollowers = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const theme = useTheme();
    const [tempUsernames, setTempUsernames] = useState(() => {
        let usernames = searchParams.get("usernames") ?? "";

        usernames = usernames
            .split(",")
            .map((i) => i.trim().toLowerCase())
            .filter((i) => !!i);

        if (usernames.length >= 2) {
            return usernames;
        }

        return ["", ""];
    });
    const [showResults, setShowResults] = useState(false);
    const [apiResponse, setApiResponse] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const platformTabs = ["Instagram", "YouTube"];
    const [tabValue, setTabValue] = useState(() => {
        const platform = searchParams.get("platform");
        return !platform
            ? 0
            : (platformTabs.findIndex(
                  (i) => i.toLowerCase() === platform.toLowerCase()
              ) ?? 0);
    });
    const executeApiRequest = useAuthApi();
    const timeoutRef = useRef(null);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setTempUsernames(["", ""]);
        navigate(`?platform=${platformTabs[newValue].toLowerCase()}`, {
            replace: true,
            relative: true,
        });
    };

    const handleAddUsername = () => {
        if (tempUsernames.length < 10) {
            setTempUsernames((prev) => [...prev, ""]);
        }
    };

    const handleRemoveUsername = (index) => {
        setTempUsernames((prev) => prev.filter((_, i) => i !== index));
    };

    const handleUsernameChange = (index, value) => {
        setTempUsernames((prev) => {
            const newUsernames = [...prev];
            newUsernames[index] = value;
            return newUsernames;
        });
    };

    useEffect(() => {
        if (searchParams.get("tab") === "result") {
            handleContinue();
        }
    }, []);

    const handleContinue = async () => {
        setIsLoading(true);
        setError(null);
        const validUsernames = tempUsernames.filter(
            (username) => username.trim() !== ""
        );
        navigate(
            `?platform=${platformTabs[
                tabValue
            ].toLowerCase()}&usernames=${validUsernames
                .join(",")
                .toLowerCase()}`,
            {
                replace: true,
                relative: true,
            }
        );

        try {
            const result = await executeApiRequest({
                field: "handles",
                value: validUsernames.join(", "),
                apiEndpoint: `${API_ENDPOINTS.OVERLAP_DATA}${platformTabs[
                    tabValue
                ].toLowerCase()}/`,
                method: "POST",
                preprocessData: (field, value) => {
                    const formData = new FormData();
                    formData.append(field, value);
                    return formData;
                },
            });

            if (result.success && !result.data.error) {
                setApiResponse(result.data);
                setShowResults(true);
                navigate(
                    `?platform=${platformTabs[
                        tabValue
                    ].toLowerCase()}&usernames=${validUsernames
                        .join(",")
                        .toLowerCase()}&tab=result`,
                    {
                        replace: true,
                        relative: true,
                    }
                );
            } else {
                setError(
                    result.message ||
                        result.data.error.message ||
                        "An error occurred while fetching data."
                );
            }
        } catch (error) {
            setError(
                "An error occurred while fetching data. Please try again."
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container maxWidth="lg">
            <Typography variant="featureEmphasis">Overlap Followers</Typography>
            <Card
                sx={{
                    p: 3,
                    maxWidth: 1200,
                    mx: "auto",
                    mt: 4,
                    borderRadius: 2,
                }}
            >
                {showResults ? (
                    <OverlapData
                        data={apiResponse}
                        onEdit={() => {
                            setShowResults(false);
                            navigate(
                                `?platform=${platformTabs[
                                    tabValue
                                ].toLowerCase()}&usernames=${tempUsernames
                                    .join(",")
                                    .toLowerCase()}`,
                                {
                                    replace: true,
                                    relative: true,
                                }
                            );
                        }}
                    />
                ) : (
                    <>
                        <Typography variant="contentAccent" gutterBottom>
                            Overlap Followers
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                        >
                            Add multiple usernames to compare their followers
                            and find overlapping followers
                        </Typography>
                        <PlatformTabs
                            tabs={platformTabs}
                            value={tabValue}
                            onChange={handleTabChange}
                        />
                        <Alert
                            severity="warning"
                            sx={{
                                width: "fit-content",

                                my: 3,
                                backgroundColor: "rgb(255, 244, 229)",
                                color: "rgb(102, 60, 0)",
                                "& .MuiAlert-icon": {
                                    color: "rgb(102, 60, 0)",
                                },
                            }}
                        >
                            Please make sure that you are searching correct
                            usernames as you have limited access to search users
                            on daily basis.
                        </Alert>
                        {platformTabs.map((platform, index) => (
                            <TabPanel
                                key={index}
                                value={tabValue}
                                index={index}
                            >
                                <InputFields
                                    platform={platform}
                                    tempUsernames={tempUsernames}
                                    handleUsernameChange={handleUsernameChange}
                                    handleRemoveUsername={handleRemoveUsername}
                                />
                            </TabPanel>
                        ))}
                        {isLoading ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    mt: 3,
                                }}
                            >
                                <CircularProgress size={40} />
                            </Box>
                        ) : (
                            <>
                                {tempUsernames.length < 10 && (
                                    <Button
                                        startIcon={<AddIcon />}
                                        onClick={handleAddUsername}
                                        sx={{
                                            mt: 2,
                                            color: theme.palette.graph.cyan,
                                            textTransform: "initial",
                                        }}
                                    >
                                        <u>Add Username</u>
                                    </Button>
                                )}
                                <br />

                                <Button
                                    variant="contained"
                                    onClick={handleContinue}
                                    sx={{
                                        mt: 3,
                                        bgcolor: "#2E1F7A",
                                        borderRadius: "-20px",
                                        py: 1.5,
                                        "&:hover": { bgcolor: "#251963" },
                                        width: "20rem",
                                    }}
                                >
                                    Continue
                                </Button>
                            </>
                        )}
                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}
                    </>
                )}
            </Card>
        </Container>
    );
};

export default OverlapFollowers;
